/**
 * The two advisory engine mirrors a row filter needs: the emitted SQL for one
 * predicate tree (M1) and the cross-policy fold (M2), both from `03` §3.14.
 *
 * **These are second implementations of logic that lives in Sluice's engine**, and
 * the document names them as a deliberate, bounded exemption to "the control plane
 * never decides a query". The argument is that a preview is not a decision: nothing
 * in any request path consults them, no denial and no allow is ever produced here,
 * and an instance whose engine disagrees still enforces its own answer.
 *
 * What we owe in exchange, and what this module's callers must honour: every panel
 * built on it carries the words *advisory preview — the instance's engine decides*
 * in its header, not in a tooltip. The other half of the obligation is a
 * golden-fixture drift test gating the pinned-image bump, which lives in Go.
 *
 * M1 mirrors `internal/rewriter/inject_filter.go:199-219, 290-330, 381-385`.
 * M2 mirrors `internal/policy/conflict.go:149-203`.
 *
 * The emitted SQL is why `SC-F512` calls M1 load-bearing: it is the only way an
 * author can check the three operator surprises — `Like` keeping `%` and `_`
 * active, `StartsWith` / `EndsWith` / `Contains` being literal functions, `Matches`
 * being a **partial** regex — without deploying.
 */

import { shapeOf, valueText, type Predicate, type ScalarValue } from "./model.js";

/**
 * SQL_FORMS is the operator → emitted SQL mapping.
 *
 * It is a table rather than a read of the schema's per-value help, even though that
 * help carries the same strings: the help is a sentence written for an author, and
 * the preview needs structure — how many parameters each form binds, and where they
 * sit. Deriving one from the other by string surgery would make a copy-edit to the
 * help text change the rendered SQL.
 *
 * `params` is how many `$N` placeholders the form binds. **Every value binds as a
 * parameter; nothing is interpolated into SQL text**, which is the one property of
 * the emitted SQL an author must be able to rely on.
 */
const SQL_FORMS: Record<string, { params: number; render: (col: string, p: string[]) => string }> = {
  Equals: { params: 1, render: (c, p) => `${c} = ${p[0]}` },
  NotEquals: { params: 1, render: (c, p) => `${c} <> ${p[0]}` },
  GreaterThan: { params: 1, render: (c, p) => `${c} > ${p[0]}` },
  GreaterThanOrEqual: { params: 1, render: (c, p) => `${c} >= ${p[0]}` },
  LessThan: { params: 1, render: (c, p) => `${c} < ${p[0]}` },
  LessThanOrEqual: { params: 1, render: (c, p) => `${c} <= ${p[0]}` },
  Between: { params: 2, render: (c, p) => `${c} BETWEEN ${p[0]} AND ${p[1]}` },
  Like: { params: 1, render: (c, p) => `${c} ~~ ${p[0]}` },
  NotLike: { params: 1, render: (c, p) => `${c} !~~ ${p[0]}` },
  IsNull: { params: 0, render: (c) => `${c} IS NULL` },
  IsNotNull: { params: 0, render: (c) => `${c} IS NOT NULL` },
  StartsWith: { params: 1, render: (c, p) => `starts_with(${c}, ${p[0]})` },
  EndsWith: { params: 1, render: (c, p) => `ends_with(${c}, ${p[0]})` },
  Contains: { params: 1, render: (c, p) => `contains(${c}, ${p[0]})` },
  Matches: { params: 1, render: (c, p) => `regexp_matches(${c}, ${p[0]})` },
};

/** In and NotIn bind one parameter per value, so they are rendered separately. */
const LIST_FORMS: Record<string, (col: string, params: string[]) => string> = {
  In: (c, p) => `${c} IN (${p.join(", ")})`,
  NotIn: (c, p) => `${c} NOT IN (${p.join(", ")})`,
};

/** paramsFor reports how many placeholders a leaf binds, so a caller can size a
 * value list without duplicating the table. */
export function paramsFor(op: string, valueCount: number): number {
  if (op in LIST_FORMS) return Math.max(valueCount, 1);
  return SQL_FORMS[op]?.params ?? 0;
}

/**
 * predicateSql renders a tree as the SQL Sluice would inject, with parameters
 * elided as `$N`.
 *
 * Nodes are walked in the order the engine branches — `all`, `any`, `not`, then the
 * leaf — so a node that illegally populates more than one shape previews exactly as
 * the engine would read it, with the discarded half absent. That is not a
 * limitation of the preview; it is the defect the preview makes visible.
 */
export function predicateSql(node: Predicate | undefined): string {
  if (!node) return "";
  const counter = { next: 1 };
  return render(node, counter, false);
}

function render(node: Predicate, counter: { next: number }, nested: boolean): string {
  switch (shapeOf(node)) {
    case "all": {
      const parts = (node.all ?? []).map((c) => render(c, counter, true)).filter((s) => s !== "");
      if (parts.length === 0) return "";
      const joined = parts.join(" AND ");
      return nested && parts.length > 1 ? `(${joined})` : joined;
    }
    case "any": {
      const parts = (node.any ?? []).map((c) => render(c, counter, true)).filter((s) => s !== "");
      if (parts.length === 0) return "";
      const joined = parts.join(" OR ");
      // A disjunction is always parenthesized when nested: an unbracketed OR inside
      // an AND is a different filter, and this preview must not read as one.
      return nested && parts.length > 1 ? `(${joined})` : joined;
    }
    case "not": {
      const inner = node.not ? render(node.not, counter, true) : "";
      return inner === "" ? "" : `NOT ${inner}`;
    }
    default:
      return renderLeaf(node, counter);
  }
}

function renderLeaf(node: Predicate, counter: { next: number }): string {
  const column = node.column ?? "";
  const op = node.op ?? "";
  if (column === "" || op === "") return "";
  // The column is emitted as ONE quoted identifier. A dotted value therefore
  // becomes a single identifier that does not exist and breaks the query at
  // execution time — which is why the preview quotes it exactly as the rewriter
  // would rather than splitting it for readability.
  const quoted = `"${column.replace(/"/g, '""')}"`;
  if (op in LIST_FORMS) {
    const count = Math.max((node.values ?? []).length, 1);
    const params = take(counter, count);
    return LIST_FORMS[op]!(quoted, params);
  }
  const form = SQL_FORMS[op];
  if (!form) return "";
  return form.render(quoted, take(counter, form.params));
}

function take(counter: { next: number }, n: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(`$${counter.next++}`);
  return out;
}

/** describeValues renders the literal values beside the preview, so a reader can
 * see which parameter carries what without the preview interpolating them. */
export function describeValues(node: Predicate | undefined): string[] {
  if (!node) return [];
  const out: string[] = [];
  walkLeaves(node, (leaf) => {
    if (leaf.values?.length) out.push(...leaf.values.map(literal));
    else if (leaf.value !== undefined) out.push(literal(leaf.value));
  });
  return out;
}

function walkLeaves(node: Predicate, visit: (leaf: Predicate) => void) {
  switch (shapeOf(node)) {
    case "all":
      for (const c of node.all ?? []) walkLeaves(c, visit);
      return;
    case "any":
      for (const c of node.any ?? []) walkLeaves(c, visit);
      return;
    case "not":
      if (node.not) walkLeaves(node.not, visit);
      return;
    default:
      visit(node);
  }
}

function literal(v: ScalarValue): string {
  return typeof v === "string" ? JSON.stringify(v) : valueText(v);
}

/** FoldMember is one `RowFilterPolicy` in the fold, in snapshot order. */
export interface FoldMember {
  name: string;
  priority: number;
  /** `restrictive` or `permissive`, as the schema's `Combine` enum spells it. */
  combine: string;
  /** True for the policy the editor is showing, so the panel can mark it. */
  self?: boolean;
  /** True when the policy is in a shadow mode, so it participates in the order and
   * changes no outcome. */
  shadow?: boolean;
}

/**
 * foldExpression composes the fold the way `combinePredicates` does.
 *
 * The rule that surprises everyone: **each policy's own `combine` decides how *it*
 * attaches**, not how the earlier ones combined. So `A(restrictive)` followed by
 * `B(permissive)` yields `A OR B` — the second policy has undone the first — while
 * the reverse order yields `B AND A`. That is a security-relevant difference
 * produced by evaluation order, and the order is priority descending then name
 * ascending, which no pair of numbers on a form makes visible.
 *
 * A shadow policy is skipped: it is evaluated and changes no outcome, so including
 * it in the composed expression would describe a filter the gateway does not apply.
 */
export function foldExpression(members: FoldMember[]): string {
  const acting = members.filter((m) => !m.shadow);
  if (acting.length === 0) return "";
  let expr = acting[0]!.name;
  for (const m of acting.slice(1)) {
    expr = m.combine === "permissive" ? `(${expr}) OR ${m.name}` : `(${expr}) AND ${m.name}`;
  }
  return expr;
}
